"use client";

import { cn } from "@/lib/utils";
import { Sparkles } from "lucide-react";
import * as React from "react";

/** Normalize suggestion text: strip bullets, numbers, quotes, markdown. Max length for display. */
export function normalizeSuggestionText(text: string, maxLength: number = 56): string {
  if (typeof text !== "string") return "";
  let s = text.trim();
  // Strip leading bullet/dash (including unicode bullet)
  s = s.replace(/^\s*[-*•]\s*/, "");
  // Strip leading number (1. or 1) )
  s = s.replace(/^\s*\d+[.)]\s*/, "");
  // Strip surrounding quotes
  s = s.replace(/^['"]|['"]$/g, "");
  // Strip markdown bold/italic
  s = s.replace(/\*\*?|__?/g, "").trim();
  s = s.trim();
  if (s.length > maxLength) {
    s = s.slice(0, maxLength - 1).trim() + "…";
  }
  return s;
}

export function Suggestions({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "flex w-full flex-wrap items-center gap-2",
        className
      )}
      {...props}
    >
      <Sparkles className="h-3.5 w-3.5 text-[color:var(--nt-primary)] shrink-0 opacity-60" />
      {children}
    </div>
  );
}

export function Suggestion({
  suggestion,
  onClick,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  suggestion: string;
}) {
  const displayText = normalizeSuggestionText(suggestion);

  return (
    <button
      onClick={onClick}
      type="button"
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full border px-3 py-1.5 text-xs font-medium transition-all duration-150",
        "bg-[color:var(--card-bg)] border-[color:var(--card-border)] text-[color:var(--foreground-muted)]",
        "hover:border-[color:var(--nt-primary)]/40 hover:text-[color:var(--nt-primary)] hover:bg-[color:var(--nt-primary)]/5",
        "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--nt-primary)]",
        "disabled:pointer-events-none disabled:opacity-50",
        "cursor-pointer",
        "max-w-[min(100%,16rem)] truncate",
        className
      )}
      title={displayText}
      {...props}
    >
      {displayText || suggestion}
    </button>
  );
}

